import { z } from "zod";

import { dateSchema } from './utils';

enum ExportStatus {
  FINISHED = "finished",
  PROCESSING = "processing",
}

enum ResourceType {
  STATUSES = "statuses",
  DIRECT_MESSAGES = "direct_messages",
  MEDIA_ATTACHMENTS = "media_attachments",
  UPVOTES = "upvotes",
}
const resourceTypeSchema = z
  .nativeEnum(ResourceType)
  .catch(ResourceType.STATUSES);

const baseDataExportSchema = z.object({
  id: z.string(),
  account_id: z.string().catch(""),
  start_date: z.string().catch(new Date().toISOString().split("T")[0]),
  end_date: z.string().catch(new Date().toISOString().split("T")[0]),
  resource_type: resourceTypeSchema,
  status: z.nativeEnum(ExportStatus).catch(ExportStatus.PROCESSING),
  export_file_name: z.string().optional().catch(undefined),
  export_content_type: z.string().optional().catch(undefined),
  export_file_size: z.number().optional().catch(0),
  export_updated_at: dateSchema.optional().catch(undefined),
  created_at: dateSchema,
  expires_on: z.string().optional().catch(undefined),
});

const availableDataExportSchema = baseDataExportSchema.extend({
  status: z.literal(ExportStatus.FINISHED),
  export_file_name: z.string().catch("export.zip"),
  export_content_type: z.string().catch("application/zip"),
  export_file_size: z.number().catch(0),
  export_updated_at: dateSchema,
});

const pendingDataExportSchema = baseDataExportSchema.extend({
  status: z.literal(ExportStatus.PROCESSING),
  export_file_name: z.null(),
  export_content_type: z.null(),
  export_file_size: z.null(),
  export_updated_at: dateSchema.nullable().catch(null),
});

const downloadUrlSchema = z.object({
  download_url: z.string().url().catch(""),
});

const dataExportSchema = z.discriminatedUnion("status", [
  availableDataExportSchema,
  pendingDataExportSchema,
]);

type DataExport = z.infer<typeof dataExportSchema>;
type AvailableDataExport = z.infer<typeof availableDataExportSchema>;
type PendingDataExport = z.infer<typeof pendingDataExportSchema>;
type DownloadUrl = z.infer<typeof downloadUrlSchema>;
type DataResourceType = z.infer<typeof resourceTypeSchema>;

export {
  dataExportSchema,
  availableDataExportSchema,
  pendingDataExportSchema,
  downloadUrlSchema,
  type DataExport,
  type AvailableDataExport,
  type PendingDataExport,
  type DownloadUrl,
  type DataResourceType,
  ResourceType,
  ExportStatus,
};
